/**
 * Server-provided runtime configuration.
 *
 * Three things this surface needs are owned by the server and are not on any
 * public core route:
 *
 * 1. **The commerce capability gate.** `CommercePolicy.CheckoutEnabled` lives in
 *    core's process configuration. WP04's stop condition is that production
 *    checkout stays visibly unavailable until an operator enables it, so the
 *    storefront must be able to render that state *before* anyone fills in an
 *    address and gets a 503.
 * 2. **The shipping options.** Core validates `shipping_option_id` against
 *    `CommercePolicy.ShippingOptions` and rejects a command whose
 *    `shipping_minor` and `insurance_minor` do not match the policy exactly.
 *    There is no public route that lists them, so the server injects them.
 * 3. **The collector tier.** MASTER_SPEC and this packet both require the tier
 *    to be *verified server-side*. It is rendered read-only and is never an
 *    input: `checkoutIdentity` in core recomputes the discount from
 *    `customer_tier_assignments` regardless of anything the browser says.
 *
 * The document carries it as a JSON `<script type="application/json">` block —
 * the same bootstrap idiom `apps/admin` uses for its CSRF token. It is parsed
 * with the same strictness as a core response: unknown keys are rejected, and
 * a missing or malformed block leaves checkout switched off with a stated
 * reason rather than defaulting to on.
 */

export const CONFIG_ELEMENT_ID = "lp-storefront-config";

export interface ShippingOption {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly shipping_minor: number;
  readonly insurance_minor: number;
}

export interface CollectorTier {
  readonly label: string;
  readonly discount_bps: number;
}

export interface LegalPlaceholders {
  readonly returns: string;
  readonly shipping: string;
  readonly privacy: string;
  readonly terms: string;
}

export interface StorefrontConfig {
  readonly project_name: string;
  readonly canonical_origin: string;
  readonly checkout_enabled: boolean;
  readonly account_enabled: boolean;
  readonly shipping_options: readonly ShippingOption[];
  /** Present only for a signed-in collector whose tier the server verified. */
  readonly collector_tier: CollectorTier | null;
  readonly legal: LegalPlaceholders;
  /** Non-null when the block was missing or unusable; checkout stays off. */
  readonly problem: string | null;
}

/**
 * The state the app runs in when the server injected nothing. Every commerce
 * affordance is off and says why. This is also what a static preview build
 * renders, so "checkout looks available but is not" has no code path.
 */
export const UNCONFIGURED: StorefrontConfig = {
  project_name: "[PROJECT NAME]",
  canonical_origin: "",
  checkout_enabled: false,
  account_enabled: false,
  shipping_options: [],
  collector_tier: null,
  legal: {
    returns: "Return policy is not published yet.",
    shipping: "Shipping policy is not published yet.",
    privacy: "Privacy policy is not published yet.",
    terms: "Terms are not published yet."
  },
  problem: "This page was served without a storefront configuration block, so checkout is switched off."
};

const MAX_OPTIONS = 8;
const MAX_MINOR = 10_000_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function boundedText(value: unknown, maximum: number): string | null {
  if (typeof value !== "string" || value.length < 1 || value.length > maximum) return null;
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;
  return value;
}

function boundedMinor(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) return null;
  if (value < 0 || value > MAX_MINOR) return null;
  return value;
}

function unknownKey(value: Record<string, unknown>, allowed: readonly string[]): string | null {
  const permitted = new Set(allowed);
  for (const key of Object.keys(value)) if (!permitted.has(key)) return key;
  return null;
}

function parseLegal(raw: unknown): LegalPlaceholders | null {
  if (!isRecord(raw)) return null;
  if (unknownKey(raw, ["returns", "shipping", "privacy", "terms"]) !== null) return null;
  const returns = boundedText(raw["returns"], 4_000);
  const shipping = boundedText(raw["shipping"], 4_000);
  const privacy = boundedText(raw["privacy"], 4_000);
  const terms = boundedText(raw["terms"], 4_000);
  if (returns === null || shipping === null || privacy === null || terms === null) return null;
  return { returns, shipping, privacy, terms };
}

export function parseStorefrontConfig(raw: unknown): StorefrontConfig {
  const refuse = (problem: string): StorefrontConfig => ({ ...UNCONFIGURED, problem });

  if (!isRecord(raw)) return refuse("The storefront configuration block is not an object, so checkout is switched off.");
  const unexpected = unknownKey(raw, [
    "project_name",
    "canonical_origin",
    "checkout_enabled",
    "account_enabled",
    "shipping_options",
    "collector_tier",
    "legal",
    // The server injects the already-normalized StorefrontConfig. `problem`
    // is therefore part of the round trip, but is recomputed below rather than
    // trusted as capability state from the document.
    "problem"
  ]);
  if (unexpected !== null) {
    return refuse(`The storefront configuration block carried an unexpected field, so checkout is switched off.`);
  }
  const reportedProblem = raw["problem"];
  if (
    reportedProblem !== undefined &&
    reportedProblem !== null &&
    boundedText(reportedProblem, 500) === null
  ) {
    return refuse("The storefront configuration problem is unusable, so checkout is switched off.");
  }

  const projectName = boundedText(raw["project_name"], 120) ?? UNCONFIGURED.project_name;
  // An omitted origin normalizes to the empty string for static previews. The
  // normalized server object must survive the same parser during hydration.
  const canonical =
    raw["canonical_origin"] === undefined || raw["canonical_origin"] === ""
      ? ""
      : boundedText(raw["canonical_origin"], 300);
  if (canonical === null) return refuse("The configured canonical origin is unusable, so checkout is switched off.");
  if (canonical.length > 0) {
    try {
      if (new URL(canonical).protocol !== "https:") {
        return refuse("The configured canonical origin is not https, so checkout is switched off.");
      }
    } catch {
      return refuse("The configured canonical origin is not a URL, so checkout is switched off.");
    }
  }

  const legal = parseLegal(raw["legal"]) ?? UNCONFIGURED.legal;

  const rawOptions = raw["shipping_options"];
  const options: ShippingOption[] = [];
  if (rawOptions !== undefined) {
    if (!Array.isArray(rawOptions) || rawOptions.length > MAX_OPTIONS) {
      return refuse("The configured shipping options are unusable, so checkout is switched off.");
    }
    for (const entry of rawOptions) {
      if (!isRecord(entry)) return refuse("A configured shipping option is unusable, so checkout is switched off.");
      if (unknownKey(entry, ["id", "label", "detail", "shipping_minor", "insurance_minor"]) !== null) {
        return refuse("A configured shipping option carried an unexpected field, so checkout is switched off.");
      }
      const id = boundedText(entry["id"], 80);
      const label = boundedText(entry["label"], 120);
      const detail = boundedText(entry["detail"], 300);
      const shippingMinor = boundedMinor(entry["shipping_minor"]);
      const insuranceMinor = boundedMinor(entry["insurance_minor"]);
      if (id === null || label === null || detail === null || shippingMinor === null || insuranceMinor === null) {
        return refuse("A configured shipping option is incomplete, so checkout is switched off.");
      }
      options.push({ id, label, detail, shipping_minor: shippingMinor, insurance_minor: insuranceMinor });
    }
  }
  if (new Set(options.map((option) => option.id)).size !== options.length) {
    return refuse("The configured shipping options are not unique, so checkout is switched off.");
  }

  let tier: CollectorTier | null = null;
  const rawTier = raw["collector_tier"];
  if (rawTier !== undefined && rawTier !== null) {
    if (!isRecord(rawTier) || unknownKey(rawTier, ["label", "discount_bps"]) !== null) {
      return refuse("The verified collector tier is unusable, so checkout is switched off.");
    }
    const label = boundedText(rawTier["label"], 80);
    const bps = rawTier["discount_bps"];
    // Core caps a tier at 5000 bps in `checkoutIdentity`; anything else is a
    // tier this surface refuses to display rather than one it clamps quietly.
    if (label === null || typeof bps !== "number" || !Number.isSafeInteger(bps) || bps < 0 || bps > 5_000) {
      return refuse("The verified collector tier is out of bounds, so checkout is switched off.");
    }
    tier = { label, discount_bps: bps };
  }

  const checkoutEnabled = raw["checkout_enabled"] === true;
  const accountEnabled = raw["account_enabled"] === true;

  /*
   * A checkout that is switched on but has no shipping option cannot produce a
   * command core will accept, so it is treated as switched off with the honest
   * reason rather than as a form that fails on submit.
   */
  if (checkoutEnabled && options.length === 0) {
    return {
      project_name: projectName,
      canonical_origin: canonical,
      checkout_enabled: false,
      account_enabled: accountEnabled,
      shipping_options: [],
      collector_tier: tier,
      legal,
      problem: "No shipping option is configured, so checkout on this site is switched off."
    };
  }

  return {
    project_name: projectName,
    canonical_origin: canonical,
    checkout_enabled: checkoutEnabled,
    account_enabled: accountEnabled,
    shipping_options: options,
    collector_tier: tier,
    legal,
    problem: checkoutEnabled ? null : "Checkout on this site is switched off by the operator."
  };
}

/** Reads and removes the bootstrap block. Called once, at hydration. */
export function readStorefrontConfig(documentRef: Document): StorefrontConfig {
  const element = documentRef.getElementById(CONFIG_ELEMENT_ID);
  if (element === null || element.textContent === null || element.textContent.length === 0) {
    return UNCONFIGURED;
  }
  try {
    return parseStorefrontConfig(JSON.parse(element.textContent));
  } catch {
    return { ...UNCONFIGURED, problem: "The storefront configuration block is not valid JSON, so checkout is switched off." };
  }
}
